import { AssistantAvatar } from "@components/Avatar";
import type { ToolArtifact } from "@models/chat";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import {
  Box,
  ButtonBase,
  Collapse,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import type { ActivityEntry } from "@stores/chatStore";
import { useEffect, useRef, useState } from "react";
import { ArtifactView } from "./ArtifactView";
import { MarkdownRenderer } from "./MarkdownRenderer";

interface StreamingMessageProps {
  content: string;
  activity: ActivityEntry[];
  /**
   * Transient reassurance string, shown OVER the activity row for a few
   * seconds at a time when a turn is taking unusually long. When non-null
   * it takes over the activity display; when it clears, the latest real
   * activity entry takes back over automatically. Null during normal
   * short-turn behaviour so tool-call activity remains uninterrupted.
   */
  transientStatus?: string | null;
  /**
   * Image-kind artifacts the agent has emitted DURING this streaming
   * turn — surfaced inline so the user sees the chart / schematic the
   * moment the tool finishes, instead of waiting for the post-`done`
   * hydration that swaps StreamingMessage out for the persisted
   * ChatMessage. Empty array on text-only turns.
   */
  liveArtifacts?: ToolArtifact[];
}

/**
 * Hand-tuned friendly labels for commonly-called tools so the activity
 * feed reads like work-in-progress prose instead of an internal API
 * trace. Falls back to a humanised version of the raw tool name when
 * we don't have a hand-tuned label, so adding a new tool on the
 * backend doesn't break the rendering — it just shows up looking
 * a little less polished until someone adds a mapping here.
 */
const TOOL_LABELS: Record<string, { running: string; done: string }> = {
  read_datasheet: { running: "Reading datasheet", done: "Read datasheet" },
  search_parts: { running: "Searching parts", done: "Searched parts" },
  search_datasheets: {
    running: "Searching datasheets",
    done: "Searched datasheets",
  },
  search_components: {
    running: "Searching components",
    done: "Searched components",
  },
  get_part_details: { running: "Loading part", done: "Loaded part" },
  compare_parts: { running: "Comparing parts", done: "Compared parts" },
  find_alternative: {
    running: "Finding alternative",
    done: "Found alternative",
  },
  check_design_fit: { running: "Checking design fit", done: "Checked design" },
  analyze_image: { running: "Analyzing image", done: "Analyzed image" },
  prefetch_datasheets: {
    running: "Prefetching datasheets",
    done: "Prefetched datasheets",
  },
  trace_net: { running: "Tracing net", done: "Traced net" },
  generate_bom: { running: "Building BOM", done: "Built BOM" },
  render_circuit: { running: "Drawing schematic", done: "Drew schematic" },
  render_finding: { running: "Plotting finding", done: "Plotted finding" },
  tool_search_tool_bm25: {
    running: "Searching toolbox",
    done: "Searched toolbox",
  },
};

/** Humanise a snake_case tool name for the fallback path. */
function humaniseToolName(name: string): string {
  const cleaned = name.replace(/^_+/, "").replaceAll("_", " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function friendlyToolLabel(name: string, mode: "running" | "done"): string {
  const hit = TOOL_LABELS[name];
  if (hit) return hit[mode];
  return humaniseToolName(name);
}

/**
 * Pretty-print a tool-call args dict for inline display. Keeps things on one
 * line where possible, truncates pathologically long strings, and falls back
 * to JSON when the shape is unusual. Mirrors what the backend already clipped
 * — see `_summarise_tool_args` in backend/chat/service.py.
 */
function formatArgs(args?: Record<string, unknown>): string {
  if (!args) return "";
  const entries = Object.entries(args);
  if (entries.length === 0) return "";
  const parts = entries.map(([k, v]) => {
    let rendered: string;
    if (typeof v === "string") {
      rendered = JSON.stringify(v);
    } else if (v === null || typeof v === "number" || typeof v === "boolean") {
      rendered = String(v);
    } else {
      try {
        rendered = JSON.stringify(v);
      } catch {
        rendered = String(v);
      }
    }
    if (rendered.length > 220) rendered = `${rendered.slice(0, 220)}…`;
    return `${k}=${rendered}`;
  });
  return parts.join(", ");
}

/**
 * Live elapsed-seconds counter that resets every time `key` changes.
 * Drives the persistent header timer next to "Circuit Forge" so the
 * user can see the agent isn't frozen during silent stretches. The
 * row-level timer it used to also feed was removed because two
 * timers ticking on the same turn was visual noise.
 */
function useElapsedSeconds(key: number | string): number {
  const [seconds, setSeconds] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  useEffect(() => {
    // Touching `key` here keeps biome's exhaustive-deps happy AND makes
    // explicit what's actually driving the timer reset — each new entry
    // (or transition between entries) restarts the seconds counter.
    void key;
    startedAtRef.current = Date.now();
    setSeconds(0);
    const id = window.setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [key]);
  return seconds;
}

interface ActivityRowProps {
  entry: ActivityEntry;
  /** Dim past entries so the latest in-progress one stands out. */
  dim?: boolean;
}

// The per-row "Thinking… · 8s" timer used to live here, but now overlaps
// with the persistent header timer (`turnSeconds` in StreamingMessage).
// Two timers ticking on the same turn was visual noise without adding
// information — the header timer is always visible (including after the
// activity panel collapses on first content arrival) so it covers the
// whole turn. The row stays clean.
function ActivityRow({ entry, dim = false }: ActivityRowProps) {
  const rowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 0.75,
    py: 0.25,
    opacity: dim ? 0.55 : 1,
    transition: (t: { motion: { fast: string } }) => `opacity ${t.motion.fast}`,
  } as const;

  if (entry.kind === "tool_call") {
    const argsText = formatArgs(entry.args);
    return (
      <Box sx={rowSx}>
        <BuildCircleOutlinedIcon
          sx={{ fontSize: 15, color: "primary.main", mt: "2px" }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {friendlyToolLabel(entry.label, "running")}
          {argsText && (
            <Box
              component="span"
              sx={{
                fontFamily: (t) => t.fonts.mono,
                fontSize: "0.72rem",
                color: "text.secondary",
                ml: 0.75,
              }}
            >
              ({argsText})
            </Box>
          )}
        </Typography>
      </Box>
    );
  }

  if (entry.kind === "tool_result") {
    const hasSummary = !!entry.summary?.trim();
    return (
      <Box sx={rowSx}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 15, color: "success.main", mt: "2px" }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {friendlyToolLabel(entry.label, "done")}
          {hasSummary ? (
            <Box component="span" sx={{ color: "text.secondary", ml: 0.75 }}>
              — {entry.summary}
            </Box>
          ) : null}
        </Typography>
      </Box>
    );
  }

  // thinking
  return (
    <Box sx={rowSx}>
      <PsychologyAltOutlinedIcon
        sx={{ fontSize: 15, color: "text.secondary", mt: "2px" }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: "0.8125rem",
          color: "text.secondary",
          fontStyle: "italic",
          wordBreak: "break-word",
          lineHeight: 1.5,
        }}
      >
        {entry.label}
      </Typography>
    </Box>
  );
}

interface ActivityTimelineProps {
  activity: ActivityEntry[];
  transientStatus: string | null;
}

/**
 * Activity timeline — Option A "Now + collapsed trail" layout.
 *
 * Single prominent row at the top showing the agent's CURRENT step
 * (with elapsed-seconds timer when in-progress), plus a collapsible
 * "N earlier steps" pill that holds every prior entry. The pill
 * auto-collapses, so even on tool-heavy turns (30+ calls) only one
 * line takes screen real estate at a time.
 *
 * Why this instead of a flat 6-row stack: research across Cursor,
 * Claude.ai, and ChatGPT shows that progressive disclosure (one
 * active surface + collapsible history) reads as "the agent is
 * working on X right now" rather than "here is a wall of internal
 * activity I have to parse". Full trace stays one click away.
 */
function ActivityTimeline({
  activity,
  transientStatus,
}: ActivityTimelineProps) {
  const [expanded, setExpanded] = useState(false);

  if (activity.length === 0) return null;

  const latest = activity[activity.length - 1] as ActivityEntry;
  const earlier = activity.slice(0, -1);
  const earlierCount = earlier.length;

  // Transient long-turn reassurance overrides the latest row's label so
  // it flows through the same prominent slot instead of needing its own
  // surface. Reverts as soon as the next real activity entry arrives.
  const latestDisplay: ActivityEntry = transientStatus
    ? { kind: "thinking", label: transientStatus, id: latest.id }
    : latest;

  return (
    <Stack
      spacing={0.5}
      role="status"
      aria-live="polite"
      aria-atomic="false"
      aria-label="Agent activity"
    >
      <ActivityRow entry={latestDisplay} dim={false} />

      {earlierCount > 0 && (
        <Box>
          <ButtonBase
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="activity-trail"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ml: 2.75,
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              color: "text.disabled",
              transition: (t) => `color ${t.motion.fast}`,
              "&:hover": { color: "text.secondary" },
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 14,
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: (t) => `transform ${t.motion.fast}`,
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {earlierCount} earlier {earlierCount === 1 ? "step" : "steps"}
            </Typography>
          </ButtonBase>

          <Collapse in={expanded} timeout={180} unmountOnExit>
            <Box
              id="activity-trail"
              sx={{
                mt: 0.5,
                ml: 2.75,
                pl: 1.5,
                borderLeft: "1px dashed",
                borderColor: "divider",
                // Cap the expanded trail so a 50-tool turn doesn't push
                // the input off-screen — anything older scrolls in place.
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              <Stack spacing={0.25}>
                {earlier.map((entry, i) => (
                  <ActivityRow
                    key={entry.id ?? `act-trail-${i}`}
                    entry={entry}
                    dim
                  />
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
}

export function StreamingMessage({
  content,
  activity,
  transientStatus,
  liveArtifacts,
}: StreamingMessageProps) {
  const hasContent = Boolean(content);
  const inlineVisuals = (liveArtifacts ?? []).filter(
    (a) =>
      a.kind === "image" ||
      a.kind === "chart" ||
      (a.content_type ?? "").startsWith("image/") ||
      (a.content_type ?? "") === "application/vnd.cf.chart+json",
  );
  const showActivity = activity.length > 0;

  // Persistent header timer — ticks for the full lifetime of this
  // StreamingMessage instance. Restores the elapsed counter that used
  // to live on the activity-row timer: once SSE streaming was wired up,
  // tokens start filling `content` within seconds of send, which flips
  // `hasContent=true` and hides the activity panel — taking the only
  // visible "agent is working" signal with it. A turn that then waits
  // 10 minutes inside Anthropic's hosted code_execution sandbox (real
  // observed latency) leaves the user with streamed text and no
  // progress indicator at all. Anchoring the timer to the message
  // header keeps it visible regardless of which UI segment is active.
  //
  // Keyed on a stable empty string so the timer never resets across
  // re-renders within the same streaming turn. The next turn gets a
  // fresh StreamingMessage instance and a fresh counter.
  const turnSeconds = useElapsedSeconds("turn");

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        gap: 1.75,
        px: { xs: 1.5, sm: 3 },
        py: 1.25,
        alignItems: "flex-start",
        // Pulsing trace rail on the left edge — reads as a signal
        // moving down a PCB track for the duration of the in-flight
        // turn. Drawn as a thin gradient column with a vertically
        // travelling highlight so it's clearly *active*, not just an
        // accent. Sits inside the row's left padding so it doesn't
        // crowd the avatar.
        "&::before": {
          content: '""',
          position: "absolute",
          left: { xs: "6px", sm: "12px" },
          top: 8,
          bottom: 8,
          width: "2px",
          borderRadius: "2px",
          background: (t) =>
            `linear-gradient(180deg, transparent, ${alpha(t.palette.primary.main, 0.55)} 20%, ${alpha(t.palette.warning.main, 0.65)} 50%, ${alpha(t.palette.primary.main, 0.55)} 80%, transparent)`,
          backgroundSize: "100% 240%",
          animation: "cf-trace-flow 2.4s linear infinite",
          pointerEvents: "none",
          opacity: 0.85,
        },
        "@keyframes cf-trace-flow": {
          "0%": { backgroundPosition: "0% 0%" },
          "100%": { backgroundPosition: "0% -240%" },
        },
      }}
    >
      <AssistantAvatar size={30} sx={{ mt: 0.25 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.5,
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.65rem",
            fontWeight: 500,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {/* Pin-1 ember while streaming. The brand reserves amber for
              "live signal, lit chip" — anchoring it to the active turn
              is the most literal expression of that promise. */}
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "warning.main",
              boxShadow: (t) =>
                `0 0 0 2px ${alpha(t.palette.warning.main, 0.18)}`,
              animation: "cf-ember-pulse 1.6s ease-in-out infinite",
              "@keyframes cf-ember-pulse": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.55, transform: "scale(0.9)" },
              },
            }}
          />
          <span>Circuit Forge</span>
          {turnSeconds >= 2 && (
            <Box
              component="span"
              sx={{
                color: "text.disabled",
                letterSpacing: "0.04em",
                // Tabular numerals so the timer doesn't reflow as digits
                // change width — keeps the eye anchored on the count.
                fontVariantNumeric: "tabular-nums",
              }}
            >
              · {turnSeconds}s
            </Box>
          )}
        </Typography>

        {/* Activity timeline — full chronological log of the in-progress
            turn instead of a single swapping line. Stays visible while
            prose streams below so tool calls remain inspectable; the
            persisted ChatMessage renders the same calls inside
            ToolCallsPanel after the turn ends. */}
        <Box
          sx={{
            overflow: "hidden",
            opacity: showActivity ? 1 : 0,
            maxHeight: showActivity ? 600 : 0,
            mb: showActivity ? 0.75 : 0,
            transition: (t) =>
              `opacity ${t.motion.base}, max-height ${t.motion.base}, margin-bottom ${t.motion.base}`,
          }}
        >
          {showActivity && (
            <ActivityTimeline
              activity={activity}
              transientStatus={transientStatus ?? null}
            />
          )}
        </Box>

        {/* Live visual artifacts — images and interactive charts.
            Renders the moment a `tool_result` SSE arrives carrying
            one, so the user sees the chart without waiting for the
            post-`done` hydration that swaps StreamingMessage out for
            the persisted ChatMessage. */}
        {inlineVisuals.map((a, i) => (
          <ArtifactView
            key={`live-art-${i}-${a.url}`}
            artifact={a}
            variant="inline"
          />
        ))}

        {hasContent && (
          <Box sx={{ position: "relative" }}>
            <MarkdownRenderer>{content}</MarkdownRenderer>
            {/* Pin-1 ember caret — the amber tip on a "lit chip" (BRAND.md
                §2). Keeps the streaming-text rhythm from the previous block
                cursor but speaks the brand language: a glowing orientation
                marker on the active die. */}
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: "0.5em",
                height: "0.5em",
                bgcolor: "warning.main",
                borderRadius: "50%",
                verticalAlign: "baseline",
                ml: "4px",
                mb: "1px",
                boxShadow: (t) =>
                  `0 0 0 2px ${alpha(t.palette.warning.main, 0.22)}, 0 0 10px ${alpha(t.palette.warning.light, 0.55)}`,
                animation: "cf-ember-blink 1.2s ease-in-out infinite",
                "@keyframes cf-ember-blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
